import { useCallback, useContext, useMemo } from 'react'
import { Outlet, To } from 'react-router-dom'

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { OneDataCollection, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import { DocumentsDataCollectionDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import DocumentURLContext from 'modules/documents/components/Documents/List/DocumentURLContext'
import { asDocuments } from 'modules/documents/components/dataCollectionAdapters'
import DraggableDatatable from 'modules/documents/components/DraggableDatatable'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { SPACES } from 'modules/documents/lib/constants'

import type { DocumentNode } from './types'
import { useFilters } from './useFilters'
import { useColumns } from './useColumns'
import { useBulkActions } from './useBulkActions'
import { useItemActions } from './useItemActions'

const SPACE = SPACES.EMPLOYEE_MY_DOCUMENTS
const ITEMS_PER_PAGE = 100

type Props = {
  folder?: Folder | null
  initialFolder?: Folder | null
  employeeId?: string
  parentRoute?: To
  onDrop: (files: File[]) => void
  multiple?: boolean
  readonly: boolean
  onUpload?: () => void
  isLoading?: boolean
}

const DataCollection = ({
  folder = null,
  initialFolder,
  employeeId,
  parentRoute,
  onDrop,
  multiple = true,
  readonly,
  isLoading = false,
}: Props) => {
  const documentURLContext = useContext(DocumentURLContext)
  const { filters, loading: filtersLoading } = useFilters()
  const { sortings, defaultSortings, columns } = useColumns()
  const { bulkActions, handleBulkAction, Modals } = useBulkActions()
  const { itemActions, ItemActionModals } = useItemActions()

  const source = useGraphqlDataCollectionSource({
    document: DocumentsDataCollectionDocument,
    paginationType: 'pages',
    perPage: ITEMS_PER_PAGE,
    includePaginationVars: false,

    mapData: (data) => {
      const edges = data?.documents?.documentsConnection?.edges ?? []
      return edges.map((edge) => edge.node)
    },

    mapVariables: ({ filters: filterState, sortings: sortingsState, search, pagination }) => {
      const filter: Record<string, unknown> = {}

      if (filterState?.createdAt) {
        const raw = filterState.createdAt
        if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
          const from = 'from' in raw && raw.from instanceof Date ? raw.from : undefined
          const to = 'to' in raw && raw.to instanceof Date ? raw.to : undefined
          if (from && to) {
            filter.createdAt = {
              between: [from.toISOString(), to.toISOString()],
            }
          }
        }
      }

      if (filterState?.signatureStatus) {
        const statuses = Array.isArray(filterState.signatureStatus)
          ? filterState.signatureStatus.filter((v): v is string => typeof v === 'string')
          : []
        if (statuses.length > 0) {
          filter.signatureStatus = { in: statuses }
        }
      }

      // Folder context: if a folder is selected, filter by it
      if (folder?.id) {
        filter.folder = { eq: folder.id }
      }

      // Employee context
      if (employeeId) {
        filter.employee = { in: [employeeId] }
      }

      return {
        space: SPACE,
        search: search || undefined,
        filter: Object.keys(filter).length > 0 ? filter : undefined,
        sortOrder: sortingsState?.[0],
        first: pagination?.perPage ?? ITEMS_PER_PAGE,
        offsetPage: pagination?.currentPage ?? 1,
        folderId: folder?.id,
        byWithoutFolder: folder === null,
      }
    },

    mapPagination: (data) => {
      const connection = data?.documents?.documentsConnection
      return {
        total: connection?.totalCount ?? 0,
      }
    },

    search: {
      enabled: true,
    },
    filters,
    sortings,
    defaultSortings,
    selectable: readonly ? undefined : (item: DocumentNode) => item.id,
    bulkActions: readonly ? undefined : bulkActions,
    itemActions,
    itemUrl: useCallback(
      (item: DocumentNode) => documentURLContext.show(item.id),
      [documentURLContext]
    ),
  })

  const { data: collectionData } = useDataCollectionData(source)
  const records = collectionData?.records
  const documentsFromCollection = useMemo(() => asDocuments(records), [records])

  const contextValue = useMemo(
    () => ({
      documentsEntity: documentsFromCollection,
      folder,
      initialFolder,
      employeeId,
      parentRoute,
      space: SPACE,
      isLoading,
    }),
    [documentsFromCollection, folder, initialFolder, employeeId, parentRoute, isLoading]
  )

  if (filtersLoading) {
    return (
      <Box alignItems="center" padding="s64">
        <Spinner />
      </Box>
    )
  }

  const content = (
    <>
      <Modals />
      <ItemActionModals />
      <Box flexDirection="column" height="full">
        <Box flexGrow paddingX="s12" paddingTop="s12">
          <OneDataCollection
            fullHeight
            emptyStates={{
              'no-data': {
                title: i18n.t('documents.documents_datatable.empty_state.blank_state.title'),
                description: '',
              },
              'no-results': {
                title: i18n.t('documents.documents_datatable.empty_state.with_filters.title'),
                description: i18n.t(
                  'documents.documents_datatable.empty_state.with_filters.description'
                ),
              },
            }}
            source={source}
            onBulkAction={readonly ? undefined : handleBulkAction}
            visualizations={[
              {
                type: 'table',
                options: {
                  allowColumnHiding: true,
                  columns,
                },
              },
            ]}
          />
        </Box>
      </Box>
      <Outlet context={contextValue} />
    </>
  )

  if (readonly) {
    return content
  }

  return (
    <DraggableDatatable onDrop={onDrop} multiple={multiple} onDragging={() => {}}>
      {content}
    </DraggableDatatable>
  )
}

export default DataCollection
